import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Received", "Being Quoted", "Quote Sent", "Completed"];

const Progress = ({ step }) => (
  <ol className="mt-4 space-y-3" data-testid="track-steps">
    {STEPS.map((s, i) => (
      <li key={s} className="flex items-center gap-3" data-testid={`track-step-${i}`} data-done={i <= step}>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i <= step ? "bg-[#1B4383] text-white" : "bg-slate-100 text-slate-400"}`}>{i < step || (i === step && i === 3) ? <Check className="h-4 w-4" /> : i + 1}</span>
        <span className={`text-sm ${i === step ? "font-bold text-[#12294B]" : i < step ? "text-slate-700" : "text-slate-400"}`}>{s}{i === step && step < 3 ? " — Current" : ""}</span>
      </li>
    ))}
  </ol>
);

export default function TrackRequest() {
  const [q] = useSearchParams();
  const { ref: pathRef } = useParams();
  const [ref, setRef] = useState(pathRef || q.get("ref") || "");
  const [email, setEmail] = useState(q.get("e") || "");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const look = async (e) => {
    e?.preventDefault(); setBusy(true); setErr(""); setR(null);
    try { const { data } = await axios.get(`${API}/public/track`, { params: { ref, email } }); setR(data); }
    catch (x) { setErr(x.response?.data?.detail || "Something Went Wrong — Please Try Again."); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (q.get("ref") && q.get("e")) look(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#0F2347]" data-testid="track-page">
      <div className="mx-auto max-w-lg px-4 pb-10 pt-8">
        <div className="mb-6 text-center text-white">
          <img src="/somcore-logo-white.png" alt="Somcore" className="mx-auto h-8" />
          <p className="mt-2 text-sm text-white/80">At The Core Of Global Resources.</p>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-xl">
          <h1 className="font-heading text-lg font-bold text-[#12294B]">Track My Request</h1>
          <form onSubmit={look} className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div><Label className="text-xs">Reference</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="SOM-OPP-0123 Or SOM-REQ-0217-2026" data-testid="track-ref" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-email" /></div>
            <Button type="submit" disabled={busy || !ref.trim() || !email.trim()} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="track-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}</Button>
          </form>
          {err && <p className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="track-error">{err}</p>}
          {r && (
            <div className="mt-5 border-t border-border pt-4" data-testid="track-result">
              <div className="font-mono text-lg font-bold text-[#1B4383]" data-testid="track-reference">{r.reference}</div>
              <div className="text-xs text-muted-foreground">{[r.submitted_at && `Submitted ${r.submitted_at.slice(0, 10)}`, r.urgency, `Handled By ${r.handled_by}`].filter(Boolean).join(" · ")}</div>
              {r.closed ? (
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="track-closed">
                  <div className="text-sm font-bold text-slate-800">Closed</div>
                  <p className="mt-1 text-sm text-slate-600">Please Contact Us If You Still Need Help.</p>
                  <div className="mt-2 text-sm text-[#1B4383]"><a href="mailto:info@somcoregroup.com" className="font-semibold">info@somcoregroup.com</a> · <a href="tel:+255669097454">+255 669 097 454</a></div>
                </div>
              ) : <Progress step={r.step} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
